import React, { useState, useEffect, useMemo } from 'react';
import {
  LuTrash2, LuMail, LuTag, LuType, LuAlignLeft, LuCalendar,
  LuCircleCheck, LuClock, LuCircle, LuSearch, LuFilter,
  LuDownload, LuX, LuHistory, LuChevronRight, LuUsers, LuMessageSquare, LuUserPlus
} from 'react-icons/lu';
import { CSVLink } from 'react-csv';
import axios from 'axios';
import moment from 'moment';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import ModalPortal from '../../components/ui/ModalPortal';
import PageHeader from '../../components/ui/PageHeader';
import EmailReminderModal from '../../components/EmailReminderModal';

const QueryManagement = ({ onCountChange, onOpenAddModal }) => {
  const [queries, setQueries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [reminderRecipient, setReminderRecipient] = useState(null);
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all' | 'manual' | 'website'

  const [showFilters, setShowFilters] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [dateFilterMode, setDateFilterMode] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    fetchQueries();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sourceFilter, filters, dateFilterMode, startDate, endDate]);

  const fetchQueries = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/queries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setQueries(response.data.data);
        if (typeof onCountChange === 'function') {
          onCountChange(response.data.data.length);
        }
      }
    } catch (error) {
      setError('Failed to fetch queries');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    setUpdatingStatus(id);
    const token = localStorage.getItem('adminToken');
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/queries/${id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchQueries();
    } catch (error) {
      setError('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this query?')) return;
    const token = localStorage.getItem('adminToken');
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/queries/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchQueries();
    } catch (error) {
      setError('Failed to delete query');
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredQueries.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredQueries.map(r => r._id));
    }
  };

  const toggleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} selected query(ies)?`)) return;

    const token = localStorage.getItem('adminToken');
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/contact/admin/queries/bulk-delete`,
        { ids: selectedIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedIds([]);
      fetchQueries();
    } catch (error) {
      setError('Failed to delete selected queries');
    }
  };

  const filteredQueries = useMemo(() => {
    return queries.filter(query => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || (
        query.email?.toLowerCase().includes(q) ||
        query.category?.toLowerCase().includes(q) ||
        query.subject?.toLowerCase().includes(q)
      );
      const matchesStatus = filters.status === 'all' || query.status === filters.status;

      if (!matchesSearch || !matchesStatus) return false;

      // Source Filter (All vs Manual vs Website)
      const isManual = Boolean(query.isManual || query.addedBy);
      if (sourceFilter === 'manual' && !isManual) return false;
      if (sourceFilter === 'website' && isManual) return false;

      if (dateFilterMode === 'today') {
        return moment(query.createdAt).isSame(moment(), 'day');
      }
      if (dateFilterMode === 'yesterday') {
        return moment(query.createdAt).isSame(moment().subtract(1, 'day'), 'day');
      }
      if (dateFilterMode === 'last7') {
        return moment(query.createdAt).isAfter(moment().subtract(7, 'days').startOf('day'));
      }
      if (dateFilterMode === 'last30') {
        return moment(query.createdAt).isAfter(moment().subtract(30, 'days').startOf('day'));
      }
      if (dateFilterMode === 'range') {
        let valid = true;
        if (startDate) {
          valid = valid && moment(query.createdAt).isSameOrAfter(moment(startDate).startOf('day'));
        }
        if (endDate) {
          valid = valid && moment(query.createdAt).isSameOrBefore(moment(endDate).endOf('day'));
        }
        return valid;
      }

      return true;
    });
  }, [queries, searchQuery, sourceFilter, filters, dateFilterMode, startDate, endDate]);

  const totalPages = Math.ceil(filteredQueries.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentQueries = useMemo(() => {
    return filteredQueries.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredQueries, indexOfFirstItem, indexOfLastItem]);

  const csvData = useMemo(() => {
    return filteredQueries.map(r => ({
      Email: r.email,
      Category: r.category,
      Subject: r.subject,
      Status: r.status,
      SubmittedAt: moment(r.createdAt).format('YYYY-MM-DD HH:mm:ss')
    }));
  }, [filteredQueries]);

  if (loading) {
    return <SkeletonLoader variant="table" rows={8} cols={5} />;
  }

  return (
    <div className="p-6 space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'All Queries', value: queries.length, icon: LuMessageSquare, iconBg: 'bg-primary-100 dark:bg-primary-900/30', iconColor: 'text-primary-600 dark:text-primary-400' },
          { label: 'Open', value: queries.filter(r => r.status === 'open').length, icon: LuCircle, iconBg: 'bg-yellow-100 dark:bg-yellow-900/30', iconColor: 'text-yellow-600 dark:text-yellow-400' },
          { label: 'In Progress', value: queries.filter(r => r.status === 'in-progress').length, icon: LuClock, iconBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-600 dark:text-blue-400' },
          { label: 'Resolved', value: queries.filter(r => r.status === 'resolved').length, icon: LuCircleCheck, iconBg: 'bg-green-100 dark:bg-green-900/30', iconColor: 'text-green-600 dark:text-green-400' },
        ].map((stat, i) => (
          <div key={i} className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 p-5 shadow-card">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl ${stat.iconBg}`}><stat.icon className={`w-5 h-5 ${stat.iconColor}`} /></div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-4">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-grow w-full flex items-center gap-3">
            <div className="relative flex-1">
              <LuSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-primary-500 w-4 h-4" />
              <input
                type="text"
                placeholder="Search email, subject, category..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-100 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all font-medium text-sm"
              />
            </div>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-xs font-bold text-gray-800 dark:text-gray-200 cursor-pointer focus:ring-2 focus:ring-indigo-500/30 outline-none transition-all whitespace-nowrap"
            >
              <option value="all">All Entry Sources</option>
              <option value="manual">🏷️ Manual Entries Only</option>
              <option value="website">🌐 Website Submissions Only</option>
            </select>
          </div>
          <div className="flex items-center gap-3 w-full md:w-auto">
            <button
              onClick={() => setShowDateFilters(!showDateFilters)}
              className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all whitespace-nowrap cursor-pointer ${showDateFilters || dateFilterMode !== 'all'
                  ? "bg-primary-50 dark:bg-primary-900/30 text-primary-600 border border-primary-200 dark:border-primary-800"
                  : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200"
                }`}
            >
              <LuCalendar />
              Date Filters {dateFilterMode !== 'all' ? `(${dateFilterMode.toUpperCase()})` : ''}
            </button>
            <button
              onClick={() => setSelectionMode(!selectionMode)}
              className={`flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all whitespace-nowrap ${selectionMode ? "bg-primary-600 text-white shadow-md shadow-primary-500/30" : "bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200"}`}
            >
              <LuCircleCheck />
              {selectionMode ? "Active" : "Select"}
            </button>
            <CSVLink
              data={csvData}
              filename={`Queries_${moment().format('YYYY-MM-DD')}.csv`}
              className="flex items-center justify-center gap-3 px-5 py-2.5 bg-green-600 text-white rounded-xl font-bold text-xs hover:bg-green-700 transition-all shadow-md shadow-green-500/30 whitespace-nowrap cursor-pointer"
            >
              <LuDownload size={18} />
              Export CSV
            </CSVLink>
          </div>
        </div>

        {showDateFilters && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-dark-700 flex flex-col md:flex-row gap-4 items-end animate-in fade-in duration-200">
            <div className="w-full md:w-auto">
              <label className="text-[10px] font-black uppercase text-gray-400 block mb-1">Date Range Preset</label>
              <select
                value={dateFilterMode}
                onChange={(e) => setDateFilterMode(e.target.value)}
                className="w-full md:w-48 px-3 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-sm text-gray-900 dark:text-white cursor-pointer"
              >
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7">Last 7 Days</option>
                <option value="last30">Last 30 Days</option>
                <option value="range">Custom Range</option>
              </select>
            </div>

            {dateFilterMode === 'range' && (
              <>
                <div className="w-full md:w-auto">
                  <label className="text-[10px] font-black uppercase text-gray-400 block mb-1">From Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-sm text-gray-900 dark:text-white"
                  />
                </div>
                <div className="w-full md:w-auto">
                  <label className="text-[10px] font-black uppercase text-gray-400 block mb-1">To Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-dark-600 bg-gray-50 dark:bg-dark-700 text-sm text-gray-900 dark:text-white"
                  />
                </div>
              </>
            )}

            {dateFilterMode !== 'all' && (
              <button
                type="button"
                onClick={() => {
                  setDateFilterMode('all');
                  setStartDate('');
                  setEndDate('');
                }}
                className="px-4 py-2 text-xs font-bold text-red-600 dark:text-red-400 underline hover:no-underline cursor-pointer"
              >
                Reset Date Filter
              </button>
            )}
          </div>
        )}
      </div>

      {selectionMode && (
        <div className="flex items-center justify-between p-4 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-2xl">
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-primary-700 dark:text-primary-300">
              {selectedIds.length} of {filteredQueries.length} selected
            </span>
            <button
              type="button"
              onClick={toggleSelectAll}
              className="text-xs font-bold text-primary-600 dark:text-primary-400 underline hover:no-underline cursor-pointer"
            >
              {selectedIds.length === filteredQueries.length && filteredQueries.length > 0 ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          {selectedIds.length > 0 && (
            <button
              type="button"
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-xs font-bold rounded-xl hover:bg-red-700 transition-colors shadow-sm cursor-pointer"
            >
              <LuTrash2 className="w-4 h-4" /> Delete Selected ({selectedIds.length})
            </button>
          )}
        </div>
      )}

      <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-max">
            <thead>
              <tr className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                {selectionMode && (
                  <th className="px-4 py-4 text-center w-12">
                    <input
                      type="checkbox"
                      checked={selectedIds.length > 0 && selectedIds.length === filteredQueries.length}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded text-primary-600 focus:ring-primary-500 cursor-pointer"
                    />
                  </th>
                )}
                <th className="px-4 py-4 text-left text-sm font-semibold">Email</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Category</th>
                <th className="px-4 py-4 text-left text-sm font-semibold max-w-[180px]">Subject</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-4 text-left text-sm font-semibold">Submitted</th>
                <th className="px-4 py-4 text-center text-sm font-semibold w-28">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
              {currentQueries.map((query) => (
                <tr
                key={query._id}
                className={`hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors cursor-pointer ${selectedIds.includes(query._id) ? 'bg-primary-50/50 dark:bg-primary-900/20' : query.isManual ? 'bg-indigo-50/30 dark:bg-indigo-950/20' : ''}`}
                onClick={() => selectionMode ? toggleSelectOne(query._id) : setSelectedQuery(query)}
              >
                {selectionMode && (
                  <td className="px-4 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(query._id)}
                      onChange={() => toggleSelectOne(query._id)}
                      onClick={(e) => e.stopPropagation()}
                      className="rounded"
                    />
                  </td>
                )}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-white text-sm whitespace-nowrap truncate max-w-[220px]" title={query.email}>{query.email}</span>
                    {(query.isManual || query.addedBy) && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-indigo-100 text-indigo-700 dark:bg-indigo-950/80 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 shadow-xs" title={`Added by ${query.addedBy || 'Admin'}`}>
                        🏷️ Manual
                      </span>
                    )}
                  </div>
                </td>
                  <td className="px-4 py-3 whitespace-nowrap"><span className="px-2 py-1 rounded-lg bg-gray-100 dark:bg-dark-700 text-gray-800 dark:text-gray-200 text-[10px] font-black uppercase">{query.category}</span></td>
                  <td className="px-4 py-3 max-w-[180px] text-sm text-gray-500 whitespace-nowrap truncate" title={query.subject}>{query.subject}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <select
                      value={query.status}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleStatusUpdate(query._id, e.target.value)}
                      className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase border transition-colors cursor-pointer ${query.status === 'open' ? 'bg-yellow-50 text-yellow-700 border-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800' :
                          query.status === 'in-progress' ? 'bg-blue-50 text-blue-700 border-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800' :
                            'bg-green-50 text-green-700 border-green-100 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800'
                        }`}
                    >
                      <option value="open">Open</option>
                      <option value="in-progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                    </select>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{moment(query.createdAt).format('MMM DD, YYYY')}</td>
                  <td className="px-4 py-3 text-center whitespace-nowrap w-28">
                    <div className="flex items-center justify-center gap-1.5">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(query._id); }}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-xl transition-colors cursor-pointer"
                        title="Delete Query"
                      >
                        <LuTrash2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setReminderRecipient({ name: query.email.split('@')[0], email: query.email }); }}
                        className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 border border-primary-200 dark:border-primary-800/50 rounded-xl transition-colors cursor-pointer"
                        title="Set Email Reminder"
                      >
                        <LuMail className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Bar */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 dark:border-dark-700 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">
              Showing <span className="font-bold text-gray-900 dark:text-white">{indexOfFirstItem + 1}</span> to <span className="font-bold text-gray-900 dark:text-white">{Math.min(indexOfLastItem, filteredQueries.length)}</span> of <span className="font-bold text-gray-900 dark:text-white">{filteredQueries.length}</span> results
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
              >
                Previous
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                    return (
                      <button
                        key={page}
                        type="button"
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 rounded-xl text-xs font-bold transition-all cursor-pointer ${currentPage === page ? 'bg-primary-600 text-white shadow-sm shadow-primary-500/30' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700'}`}
                      >
                        {page}
                      </button>
                    );
                  }
                  if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="px-1 text-xs text-gray-400">...</span>;
                  }
                  return null;
                })}
              </div>
              <button
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 rounded-xl border border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-700 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <ModalPortal open={Boolean(selectedQuery)} onClose={() => setSelectedQuery(null)}>
        {selectedQuery && (
          <>
            <div className="p-6 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Query Details</h2>
              <button onClick={() => setSelectedQuery(null)} className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-xl text-gray-500 dark:text-gray-300"><LuX /></button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto">
              <div className="p-4 bg-gray-50 dark:bg-dark-700/60 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Subject</label><p className="font-bold text-gray-900 dark:text-white">{selectedQuery.subject}</p></div>
              <div className="p-4 bg-gray-50 dark:bg-dark-700/60 rounded-2xl"><label className="text-[10px] font-black uppercase text-gray-400">Description</label><p className="font-medium italic leading-relaxed text-sm text-gray-700 dark:text-gray-200">"{selectedQuery.description}"</p></div>
            </div>
            <div className="p-6 border-t border-gray-100 dark:border-dark-700 flex justify-end gap-3">
              <button onClick={() => setSelectedQuery(null)} className="px-6 py-2 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-200 rounded-xl font-bold text-xs uppercase cursor-pointer">Close</button>
              <button onClick={() => { setReminderRecipient({ name: selectedQuery.email.split('@')[0], email: selectedQuery.email }); setSelectedQuery(null); }} className="px-6 py-2 bg-primary-600 text-white rounded-xl font-bold text-xs uppercase flex items-center gap-2 cursor-pointer"><LuMail className="w-4 h-4" /> Send Reminder</button>
              <button onClick={() => { handleDelete(selectedQuery._id); setSelectedQuery(null); }} className="px-6 py-2 bg-red-600 text-white rounded-xl font-bold text-xs uppercase cursor-pointer">Delete</button>
            </div>
          </>
        )}
      </ModalPortal>

      <EmailReminderModal
        isOpen={Boolean(reminderRecipient)}
        onClose={() => setReminderRecipient(null)}
        recipient={reminderRecipient}
      />
    </div>
  );
};

export default QueryManagement;
